// core/cssLoader.js
// Dynamic CSS loading for skill-specific styles

/** Track which CSS files have been loaded */
const loadedStyles = new Set();

/** Track loading promises to prevent duplicate loads */
const loadingPromises = new Map();

/**
 * Load a CSS file dynamically
 * @param {string} id - Unique identifier for this CSS (usually skill ID)
 * @param {string} href - Path to CSS file
 * @returns {Promise<void>}
 */
export async function loadCSS(id, href) {
    // Already loaded
    if (loadedStyles.has(id)) {
        return;
    }

    // Already loading - return existing promise
    if (loadingPromises.has(id)) {
        return loadingPromises.get(id);
    }

    const loadPromise = new Promise((resolve, reject) => {
        // Check if link already exists in DOM
        const existingLink = document.querySelector(`link[data-css-id="${id}"]`);
        if (existingLink) {
            loadedStyles.add(id);
            resolve();
            return;
        }

        // Create new link element
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.dataset.cssId = id;

        link.onload = () => {
            loadedStyles.add(id);
            loadingPromises.delete(id);
            resolve();
        };

        link.onerror = () => {
            loadingPromises.delete(id);
            reject(new Error(`Failed to load CSS: ${href}`));
        };

        document.head.appendChild(link);
    });

    loadingPromises.set(id, loadPromise);
    return loadPromise;
}

/**
 * Load skill-specific CSS
 * @param {string} skillId - Skill identifier
 * @param {string} [cssPath] - Optional path override
 * @returns {Promise<void>}
 */
export async function loadSkillCSS(skillId, cssPath) {
    const path = cssPath || `/styles/skills/${skillId}.css`;

    try {
        await loadCSS(`skill-${skillId}`, path);
    } catch (error) {
        // Skill CSS is optional - log but don't throw
        console.debug(`[CSSLoader] No CSS found for skill '${skillId}':`, error.message);
    }
}

/**
 * Unload a CSS file
 * @param {string} id - CSS identifier
 */
export function unloadCSS(id) {
    const link = document.querySelector(`link[data-css-id="${id}"]`);
    if (link) {
        link.remove();
    }
    loadedStyles.delete(id);
    loadingPromises.delete(id);
}

/**
 * Unload skill-specific CSS
 * @param {string} skillId - Skill identifier
 */
export function unloadSkillCSS(skillId) {
    unloadCSS(`skill-${skillId}`);
}

/**
 * Check if a CSS file is loaded
 * @param {string} id - CSS identifier
 * @returns {boolean}
 */
export function isCSSLoaded(id) {
    return loadedStyles.has(id);
}

/**
 * Check if skill CSS is loaded
 * @param {string} skillId - Skill identifier
 * @returns {boolean}
 */
export function isSkillCSSLoaded(skillId) {
    return loadedStyles.has(`skill-${skillId}`);
}

/**
 * Preload multiple CSS files
 * @param {Array<{id: string, href: string}>} cssFiles
 * @returns {Promise<void>}
 */
export async function preloadCSS(cssFiles) {
    await Promise.all(cssFiles.map(({ id, href }) => loadCSS(id, href)));
}

/**
 * Get all loaded CSS IDs
 * @returns {string[]}
 */
export function getLoadedCSS() {
    return Array.from(loadedStyles);
}

/**
 * Clear all loaded CSS tracking (doesn't remove from DOM)
 */
export function clearLoadedCSS() {
    loadedStyles.clear();
    loadingPromises.clear();
}

/**
 * Remove all dynamically loaded CSS from DOM
 */
export function removeAllDynamicCSS() {
    const links = document.querySelectorAll('link[data-css-id]');
    links.forEach(link => link.remove());
    loadedStyles.clear();
    loadingPromises.clear();
}

export default {
    loadCSS,
    loadSkillCSS,
    unloadCSS,
    unloadSkillCSS,
    isCSSLoaded,
    isSkillCSSLoaded,
    preloadCSS,
    getLoadedCSS,
    clearLoadedCSS,
    removeAllDynamicCSS,
};
